import { Injectable } from '@angular/core';
import { BaseApiService } from '@data-access/common/base-api.service';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { CreateUserPayload } from '@shared/interfacres/user';
import { ContactUsPayload, DMCAPayload } from '@shared/interfacres/common';


@Injectable({providedIn: 'root'})
export class UsersService extends BaseApiService {

  getPerformanceTypes() {
    const types: string[] = ["Mobile", "Touring/Festival", "Club", "Radio"]
    return of(types).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    )
  }

  create(payload: CreateUserPayload) {
    const url = `${this.apiUrl}/auth/signup`;
    return this._httpClient.post(url, payload).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  update(payload: Partial<CreateUserPayload>) {
    const url = `${this.apiUrl}/profile`;
    return this._httpClient.put(url, payload).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  getUser() {
    const url = `${this.apiUrl}/profile`;
    return this._httpClient.get(url).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  getBillingHistory() {
    const url = `${this.apiUrl}/profile/billing-history`;
    return this._httpClient.get(url).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  getCurrentSubscription() {
    const url = `${this.apiUrl}/profile/current-subscription`;
    return this._httpClient.get(url).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  startSubscription(productId: string, promoCode: string) {
    const url = `${this.apiUrl}/profile/start-subscription`;
    return this._httpClient.post(url, {productId, promoCode}).pipe(
      map((response) => this.returnResponseData(response))
    );
  }

  changeSubscriptionPlan(productId: string) {
    const url = `${this.apiUrl}/profile/change-subscription`;
    return this._httpClient.post(url, {productId}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }
  changePassword(currentPassword: string, newPassword: string) {
    const url = `${this.apiUrl}/auth/change-password`;
    return this._httpClient.post(url, {currentPassword, newPassword}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  login(email: string, password: string) {
    const url = `${this.apiUrl}/auth/signin`;
    return this._httpClient.post(url, {email, password}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  cancelSubscription() {
    const url = `${this.apiUrl}/profile/cancel-subscription`;
    return this._httpClient.post(url, {}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  getPlans() {
    const url = `${this.apiUrl}/stripe/products`;
    return this._httpClient.get(url).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  resetPassword(email: string) {
    const url = `${this.apiUrl}/auth/reset-password`;
    return this._httpClient.post(url, {email}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  setPassword(password: string, accessToken: string) {
    const url = `${this.apiUrl}/auth/set-new-password`;
    return this._httpClient.post(url, {password, access_token: accessToken}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  contactUs(payload: ContactUsPayload) {
    const url = `${this.apiUrl}/support/contact-us`;
    return this._httpClient.post(url, payload).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  sendDMCA(payload: DMCAPayload) {
    const url = `${this.apiUrl}/support/dmca`;
    return this._httpClient.post(url, payload).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

  getCheckoutLink(productId: string, email: string) {
    const url = `${this.apiUrl}/stripe/checkout-link?productId=${productId}&email=${email}`;
    return this._httpClient.get(url, {responseType: 'text'}).pipe(
      map((response) => this.returnResponseData(response)),
      catchError(this.handleError)
    );
  }

}
